import { ImageCustom } from '@/components';
import { useAddToCart, useAppDispatch, useAppSelector } from '@/hooks';
import { ICartInfoItem, IForceOpenModalVariant } from '@/models';
import QuantityHelper from '@/modules/cart/helper/QuantityHelper';
import { cartSelectors } from '@/store/reducers/cartSlice';
import { commonSelectors } from '@/store/reducers/commonSlice';
import classNames from 'classnames';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import React from 'react';
import { useSWRConfig } from 'swr';

interface ICartItemMobile {
	data: ICartInfoItem;
	id: string;
	dataShowModalVariant: IForceOpenModalVariant[];
	onShowModalVariant: React.Dispatch<React.SetStateAction<IForceOpenModalVariant[]>>;
}

const DynamicModalSelectVariant = dynamic(() => import('@/components/ModalSelectVariant'), {
	ssr: false,
});

const CartItem: React.FC<ICartItemMobile> = ({
	data,
	id,
	dataShowModalVariant,
	onShowModalVariant,
}) => {
	const dispatch = useAppDispatch();
	const { handleAddToCart, handleChangeVariationsCart, deBounceAddToCart } = useAddToCart(dispatch);
	const { mutate } = useSWRConfig();
	const { dataVariantWithId } = useAppSelector(commonSelectors);
	const { dataCart } = useAppSelector(cartSelectors);

	const router = useRouter();

	return (
		<div className='flex gap-[30px] first-of-type:pt-0 last-of-type:border-b-0 py-[30px] border-b border-gray-custom'>
			<div className='flex flex-col gap-3'>
				<div className='w-[130px] h-full aspect-square overflow-hidden'>
					<ImageCustom fill src={data?.product_image} objectFit='contain' className='!h-full' />
				</div>
				<div
					className={classNames([
						'uppercase cursor-pointer underline text-[#71717A] text-base font-bold text-center',
						dataCart?.isRequestAdd && '!cursor-not-allowed',
					])}
					onClick={() =>
						deBounceAddToCart(() =>
							handleAddToCart(
								{
									isChange: true,
									productId: `${data?.product_id}`,
									productVariationId: `${data?.variation_id}`,
									quantity: 0,
								},
								{
									showNotify: false,
									onMutate: () =>
										mutate({
											url: `/v1/cart`,
											method: 'GET',
											baseURL: process.env.NEXT_PUBLIC_DOMAIN_API_AUTH!,
										}),
								},
							),
						)
					}
				>
					xóa
				</div>
			</div>
			<div className='flex flex-col my-[calc(90px_-_65px)] flex-auto gap-3 justify-start'>
				<div
					className='tooltip w-full group-hover:tooltip-open whitespace-normal break-words before:bg-black/25 after:border-[black_transparent_transparent_transparent]'
					data-tip={data?.product_name}
				>
					<span
						onClick={() => router.push(`/chi-tiet/${data?.product_id}`)}
						className='line-clamp-1 text-left font-bold mb-[6px] cursor-pointer'
					>
						{data?.product_name}
					</span>
				</div>
				<div className='flex items-center gap-3'>
					<div className='flex flex-col gap-1'>
						<DynamicModalSelectVariant
							productIdSelect={`${data?.product_id}`}
							data={dataVariantWithId?.data!}
							defaultvariationId={`${data?.variation_id}`}
							open={Boolean(dataShowModalVariant?.find((el) => el?.key === id)?.isActive)}
							isLoading={dataVariantWithId?.isRequest}
							onActive={(isActive) => {
								onShowModalVariant((prev) => {
									const checkExisted = prev?.some((el) => el?.key === id);

									if (!checkExisted) {
										return [
											...(prev?.map((el) => ({ key: el?.key, isActive: false })) ?? []),
											{ key: id, isActive: isActive },
										];
									}
									return prev?.map((el) =>
										el?.key === id
											? { key: el?.key, isActive: isActive }
											: { key: el?.key, isActive: false },
									);
								});
							}}
							onSubmit={(idVariantSelected) => {
								handleChangeVariationsCart(
									{
										productId: `${data?.product_id}`,
										oldProductVariationId: `${data?.variation_id}`,
										newProductVariationId: idVariantSelected,
									},
									{
										showNotify: false,
										onMutate: () =>
											mutate({
												url: `/v1/cart`,
												method: 'GET',
												baseURL: process.env.NEXT_PUBLIC_DOMAIN_API_AUTH!,
											}),
									},
								);
							}}
						/>
						<p className='text-[#71717A]'>{data?.variation_name}</p>
					</div>
					<div className='gap-3 flex-auto flex flex-row items-center'>
						<div className='font-normal text-medium text-primary'>
							{Number(data?.price)?.toLocaleString('it-IT')}₫
						</div>
						<div className='flex gap-3 flex-auto'>
							<QuantityHelper
								defaultValue={data?.quantity}
								onChange={(quantity) => {
									deBounceAddToCart(
										() =>
											quantity &&
											handleAddToCart(
												{
													isChange: true,
													productId: `${data?.product_id}`,
													productVariationId: `${data?.variation_id}`,
													quantity: quantity,
												},
												{
													showNotify: false,
													onMutate: () =>
														mutate({
															url: `/v1/cart`,
															method: 'GET',
															baseURL: process.env.NEXT_PUBLIC_DOMAIN_API_AUTH!,
														}),
												},
											),
									);
								}}
								render={(quantity) => (
									<div className='flex-auto flex items-center justify-end'>
										<span className='text-medium font-bold'>
											{(+data?.price * quantity)?.toLocaleString('it-IT')}₫
										</span>
									</div>
								)}
							/>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default CartItem;
